import React, { useCallback } from 'react';
import { useMarketplaceContext } from 'modules/marketplace/context/marketplace.context';
import { ListMarketplaceItemsResponse } from 'modules/marketplace/interfaces/marketplace.interface';
import { Skeleton } from '@material-ui/lab';
import { OrderItemStatusEnum } from 'common/enums';
import { apiRedirectTo, getBackofficeRedirectUrl, getMd5Hash } from 'common/utils';
import { useHistory } from 'react-router';
import MarketplaceItemView from './marketplace-item-card-view';
import { useStyles } from './marketplace-item-card-style';

interface MarketplaceItemCardViewProps {
    item?: ListMarketplaceItemsResponse;
    type?: 'cart' | 'order' | 'judgment';
    accessType?: 'organization' | 'provider';
    orderId?: number;
    quantity?: number;
    loading?: boolean;
    isActionLoading?: boolean;
    orderItemStatus?: OrderItemStatusEnum;
    onAddToCart?: (id: number) => void;
    onRemoveFromCart?: (id: number) => void;
    onItemQuantityChange?: (value: { id: number; quantity: number }) => void;
    onAdditionalInformationChange?: (value: {
        id: number;
        itemAdditionalInformation: string;
    }) => void;
    onDeclassify?: (value: { lotId: number; providerId: number }) => void;
    isDisqualifying?: boolean;
    isJudged?: boolean;
    isFinished?: boolean;
}

const MarketplaceItemCard: React.FC<MarketplaceItemCardViewProps> = ({
    item,
    orderId,
    type = 'cart',
    accessType = 'organization',
    quantity,
    loading = false,
    isActionLoading = false,
    orderItemStatus,
    onAddToCart,
    onRemoveFromCart,
    onItemQuantityChange,
    onAdditionalInformationChange,
    onDeclassify,
    isDisqualifying = false,
    isJudged = false,
    isFinished = false,
}) => {
    const styles = useStyles();
    const { marketplaceCart } = useMarketplaceContext();

    const backofficeRedirectUrl = getBackofficeRedirectUrl();
    const loginBaseUrl = getBackofficeRedirectUrl(true);

    const history = useHistory();

    if (loading || !item) {
        return <Skeleton className={styles.skeleton} />;
    }

    const handleOnAddToCart = useCallback(() => {
        onAddToCart?.(item.id);
    }, []);

    const handleOnRemoveFromCart = useCallback(() => {
        onRemoveFromCart?.(item.id);
    }, []);

    const handleOnItemQuantityChange = useCallback((quantity: number) => {
        onItemQuantityChange?.({ id: item.id, quantity });
    }, []);

    const handleOnAdditionalInformationChange = (
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        onAdditionalInformationChange?.({
            id: item.id,
            itemAdditionalInformation: e.target.value,
        });
    };

    const handleOnDeclassify = useCallback((providerId: number) => {
        const lotId = item?.lotId;
        if (!lotId) return;

        onDeclassify?.({ lotId, providerId });
    }, []);

    const handleRedirectToProposal = () => {
        return apiRedirectTo({
            backofficeRedirectUrl,
            loginBaseUrl,
            redirectUrl: encodeURIComponent(
                `/proposta/?idE=${getMd5Hash(String(item.processId))}&order=${orderId}`
            ),
        });
    };

    const handleRedirectToAccreditation = () => {
        history.push(`/pesquisa/${item.processId}`);
    };

    const cartItem = marketplaceCart?.items.find((cartItem) => cartItem?.item?.id === item.id);
    const hasOnCart = Boolean(cartItem);

    const enableDeclassify = !isFinished && !!onDeclassify;

    return (
        <MarketplaceItemView
            item={item}
            orderId={orderId}
            type={type}
            accessType={accessType}
            quantity={quantity}
            cartItem={cartItem}
            isActionLoading={isActionLoading}
            orderItemStatus={orderItemStatus}
            hasOnCart={hasOnCart}
            onAddToCart={handleOnAddToCart}
            onItemQuantityChange={handleOnItemQuantityChange}
            onAdditionalInformationChange={handleOnAdditionalInformationChange}
            onRemoveFromCart={handleOnRemoveFromCart}
            onDeclassify={enableDeclassify ? handleOnDeclassify : undefined}
            isDisqualifying={isDisqualifying}
            isJudged={isJudged}
            onRedirectToAccreditation={handleRedirectToAccreditation}
            onRedirectToProposal={handleRedirectToProposal}
        />
    );
};

export default MarketplaceItemCard;
